import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Bot, Code2, FileText, MessageSquare, Search, Sparkles } from 'lucide-react'
import { ScrollReveal } from './ScrollReveal'

const CYCLE_MS = 5500

const useCases = [
  {
    icon: MessageSquare,
    title: 'Customer Support',
    description: 'Deploy intelligent chatbots that handle queries 24/7, reduce support tickets, and escalate complex issues seamlessly.',
    color: 'text-indigo-400',
    bg: 'bg-indigo-500/10',
    border: 'border-indigo-500/20',
    glow: 'rgba(99,102,241,0.15)',
    models: ['ns-speed', 'ns-balanced'],
  },
  {
    icon: Code2,
    title: 'Code Generation',
    description: 'Integrate AI-powered coding assistants into your IDE or CI pipeline. Autocomplete, review, and generate code at scale.',
    color: 'text-violet-400',
    bg: 'bg-violet-500/10',
    border: 'border-violet-500/20',
    glow: 'rgba(139,92,246,0.15)',
    models: ['ns-pro', 'ns-coder'],
  },
  {
    icon: FileText,
    title: 'Document Processing',
    description: 'Extract, summarize, and classify documents. Process contracts, reports, and invoices with high accuracy.',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/20',
    glow: 'rgba(16,185,129,0.15)',
    models: ['ns-balanced', 'ns-pro'],
  },
  {
    icon: Search,
    title: 'Semantic Search',
    description: 'Build search experiences that understand meaning, not just keywords. Power recommendation engines and knowledge bases.',
    color: 'text-orange-400',
    bg: 'bg-orange-500/10',
    border: 'border-orange-500/20',
    glow: 'rgba(249,115,22,0.15)',
    models: ['ns-embed', 'ns-speed'],
  },
  {
    icon: Bot,
    title: 'AI Agents',
    description: 'Orchestrate multi-step agents that browse the web, call APIs, and complete complex workflows autonomously.',
    color: 'text-rose-400',
    bg: 'bg-rose-500/10',
    border: 'border-rose-500/20',
    glow: 'rgba(244,63,94,0.15)',
    models: ['ns-pro', 'ns-balanced'],
  },
  {
    icon: Sparkles,
    title: 'Content Generation',
    description: 'Generate marketing copy, product descriptions, and localised content at scale across APAC markets and languages.',
    color: 'text-yellow-400',
    bg: 'bg-yellow-500/10',
    border: 'border-yellow-500/20',
    glow: 'rgba(234,179,8,0.15)',
    models: ['ns-speed', 'ns-balanced', 'ns-pro'],
  },
]

const models = [
  {
    id: 'ns-speed',
    name: 'NS Speed',
    tag: 'Fast',
    tagColor: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    description: 'Ultra-low latency for real-time apps',
    latency: '~80ms',
    context: '16K',
  },
  {
    id: 'ns-balanced',
    name: 'NS Balanced',
    tag: 'Popular',
    tagColor: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
    description: 'Best quality-to-cost ratio for most tasks',
    latency: '~300ms',
    context: '32K',
  },
  {
    id: 'ns-pro',
    name: 'NS Pro',
    tag: 'Powerful',
    tagColor: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
    description: 'Highest reasoning for complex workloads',
    latency: '~800ms',
    context: '128K',
  },
  {
    id: 'ns-coder',
    name: 'NS Coder',
    tag: 'Code',
    tagColor: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
    description: 'Fine-tuned for code generation and review',
    latency: '~400ms',
    context: '64K',
  },
  {
    id: 'ns-embed',
    name: 'NS Embed',
    tag: 'Embeddings',
    tagColor: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
    description: 'High-dimensional vectors for search & retrieval',
    latency: '~50ms',
    context: '8K',
  },
]

export function ModelsAndUseCases() {
  const [activeUseCase, setActiveUseCase] = useState(0)
  const [selectedModels, setSelectedModels] = useState<string[]>(['ns-balanced'])
  const [panelKey, setPanelKey] = useState(0)
  const [paused, setPaused] = useState(false)
  const [progressKey, setProgressKey] = useState(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const goTo = useCallback((i: number) => {
    setActiveUseCase(i)
    setPanelKey(k => k + 1)
    setProgressKey(k => k + 1)
  }, [])

  const next = useCallback(() => {
    goTo((activeUseCase + 1) % useCases.length)
  }, [activeUseCase, goTo])

  // Auto-cycle
  useEffect(() => {
    if (paused) return
    intervalRef.current = setInterval(next, CYCLE_MS)
    return () => clearInterval(intervalRef.current!)
  }, [paused, next])

  const handleManualSelect = (i: number) => {
    setPaused(true)
    goTo(i)
    // Resume auto-cycle after 8s of inactivity
    setTimeout(() => setPaused(false), 8000)
  }

  const toggleModel = (id: string) => {
    setSelectedModels(prev =>
      prev.includes(id)
        ? prev.length > 1 ? prev.filter(m => m !== id) : prev
        : [...prev, id]
    )
  }

  const activeModels = useCases[activeUseCase].models
  const uc = useCases[activeUseCase]

  return (
    <section className="py-24 bg-[#07090f] relative overflow-hidden">
      <div className="absolute left-0 top-1/3 w-[500px] h-[500px] bg-violet-600/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute right-0 bottom-1/3 w-[400px] h-[400px] bg-indigo-600/5 rounded-full blur-[100px] pointer-events-none" />

      {/* Dynamic background glow that matches active use case */}
      <div
        className="absolute inset-0 pointer-events-none transition-all duration-700"
        style={{ background: `radial-gradient(ellipse 600px 400px at 75% 50%, ${uc.glow}, transparent)` }}
      />

      <div className="container relative z-10">
        <ScrollReveal className="text-center max-w-2xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">One API. Every use case.</h2>
          <p className="mt-4 text-lg text-gray-400">
            Pick the right model for your workload — or let Neoserve route automatically.
          </p>
        </ScrollReveal>

        <div className="grid lg:grid-cols-2 gap-10 items-start">

          {/* Use Cases */}
          <div className="space-y-2">
            {useCases.map((u, i) => {
              const isActive = activeUseCase === i
              return (
                <ScrollReveal key={u.title} delay={i * 60}>
                  <button
                    onClick={() => handleManualSelect(i)}
                    className={`relative w-full text-left rounded-xl p-4 border overflow-hidden transition-all duration-300 ${
                      isActive
                        ? `${u.border} ${u.bg}`
                        : 'border-white/5 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/10'
                    }`}
                    style={{
                      boxShadow: isActive ? `0 0 24px ${u.glow}` : 'none',
                    }}
                  >
                    {/* Auto-progress bar */}
                    {isActive && !paused && (
                      <div
                        key={progressKey}
                        className="absolute bottom-0 left-0 h-[2px] rounded-full"
                        style={{
                          background: `linear-gradient(to right, ${u.glow.replace('0.15', '0.8')}, white)`,
                          animation: `progressBar ${CYCLE_MS}ms linear forwards`,
                        }}
                      />
                    )}

                    <div className="flex items-start gap-4">
                      <div
                        className={`inline-flex p-2.5 rounded-xl ${u.bg} border ${u.border} shrink-0 transition-transform duration-300 ${isActive ? 'scale-110' : ''}`}
                      >
                        <u.icon className={`h-5 w-5 ${u.color}`} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className={`text-sm font-semibold transition-colors duration-200 ${isActive ? 'text-white' : 'text-gray-400'}`}>
                            {u.title}
                          </h3>
                          <div className="flex gap-1">
                            {u.models.map(m => (
                              <span key={m} className={`text-xs px-1.5 py-0.5 rounded font-mono border transition-colors duration-200 ${
                                isActive ? `${u.bg} ${u.color} ${u.border}` : 'bg-white/5 text-gray-600 border-white/5'
                              }`}>
                                {m}
                              </span>
                            ))}
                          </div>
                        </div>
                        <p className={`text-xs leading-relaxed transition-colors duration-200 ${isActive ? 'text-gray-400' : 'text-gray-600'}`}>
                          {u.description}
                        </p>
                      </div>
                    </div>
                  </button>
                </ScrollReveal>
              )
            })}
          </div>

          {/* Model Panel — re-mounts on use case change for entry animation */}
          <ScrollReveal delay={200} className="sticky top-24">
            <div
              key={panelKey}
              className="rounded-2xl border border-white/10 overflow-hidden bg-white/[0.02]"
              style={{
                animation: 'panelSlideIn 0.4s cubic-bezier(0.16,1,0.3,1) forwards',
              }}
            >
              <div className="bg-white/[0.03] border-b border-white/10 px-5 py-4">
                <p className="text-sm font-semibold text-white">Select models to compare</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Recommended for{' '}
                  <span className={`font-medium ${uc.color}`}>{uc.title}</span>
                  {' '}are highlighted
                </p>
              </div>

              <div className="divide-y divide-white/5">
                {models.map((model, mi) => {
                  const isSelected = selectedModels.includes(model.id)
                  const isRecommended = activeModels.includes(model.id)
                  return (
                    <button
                      key={model.id}
                      onClick={() => toggleModel(model.id)}
                      className={`w-full text-left px-5 py-4 flex items-start gap-4 transition-all duration-200 ${
                        isSelected
                          ? 'bg-indigo-500/10'
                          : isRecommended
                          ? 'bg-white/[0.02] hover:bg-white/[0.04]'
                          : 'bg-transparent hover:bg-white/[0.02] opacity-40 hover:opacity-60'
                      }`}
                      style={{
                        animation: `panelSlideIn 0.35s cubic-bezier(0.16,1,0.3,1) ${mi * 50}ms both`,
                      }}
                    >
                      <div className={`mt-0.5 h-4 w-4 shrink-0 rounded border-2 flex items-center justify-center transition-all duration-200 ${
                        isSelected ? 'bg-indigo-600 border-indigo-600 scale-110' : 'border-white/20'
                      }`}>
                        {isSelected && (
                          <svg className="h-2.5 w-2.5 text-white" fill="none" viewBox="0 0 10 8">
                            <path d="M1 4l3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-semibold text-white">{model.name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${model.tagColor}`}>{model.tag}</span>
                          {isRecommended && (
                            <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
                              Recommended
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mb-2">{model.description}</p>
                        <div className="flex gap-4 text-xs text-gray-600">
                          <span>Latency: <span className="text-gray-400 font-medium">{model.latency}</span></span>
                          <span>Context: <span className="text-gray-400 font-medium">{model.context}</span></span>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>

              <div className="bg-white/[0.02] border-t border-white/5 px-5 py-3">
                <p className="text-xs text-gray-600">
                  {selectedModels.length} model{selectedModels.length !== 1 ? 's' : ''} selected —
                  Neoserve routes across all selected models automatically.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>

      <style>{`
        @keyframes progressBar {
          from { width: 0%; }
          to   { width: 100%; }
        }
        @keyframes panelSlideIn {
          from { opacity: 0; transform: translateY(12px) scale(0.99); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </section>
  )
}
